import { defaultJobDraftV2, OrderStateV2 } from './context';

// Pure reducers for the two orchestrator setState callbacks that re-check
// race-prone guards inside the updater. Extracted from `index.tsx` so they
// can be unit-tested independently of the hook implementation, and so a
// future React Query swap of `useCertificates` / `useRateQuote` cannot
// silently regress the "don't clobber template prefill" or "don't clobber
// user-entered rates" behavior.

// Mirrors the backend's TemplateFingerprintService#normalize_text
// (`value.to_s.strip.gsub(/\s+/, " ").downcase`) so the frontend dedupe
// keys off the exact same canonical form the server used to fingerprint
// the template — two jobs the backend considered identical must hash
// identically here too.
const normalizeTemplateText = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

// A repeat-order template persists the *submission* payload, not the
// wizard's job drafts. `buildCreatePayload` fans a single logical job
// draft out into one entry per shift/day (per-shift expansion), and the
// backend stores those expanded entries verbatim. Re-applying that
// payload as-is surfaces a single-skill repeat as N blank duplicate
// skill cards (one per original shift), each with an empty schedule —
// the spurious "added skill" card on the Submission step.
//
// Collapse entries that share the same logical job identity back into
// one. The key set is the backend TemplateFingerprintService's
// `canonical_jobs` *minus the per-shift-varying fields* (`quantity`,
// `mandatory_break_time`): the per-shift expansion stamps those onto
// every emitted clone (`perShiftQuantity`/`perShiftBreak` in
// buildCreatePayload), so a single-skill order with mixed-quantity or
// mixed-break shifts would otherwise hash to several distinct keys and
// still surface as multiple "added skill" cards on the repeat's Review
// step. The repeat flow blanks the schedule by design, so those
// per-shift values are rebuilt by the user anyway and must not split one
// logical job. The remaining fields (skill/address/contact/uniform +
// normalized instruction/flag fields) keep a genuine multi-skill /
// multi-location template's distinct entries apart. First occurrence
// wins, so order is preserved.
const templateJobIdentity = (job: Record<string, unknown>): string => {
  const certificates = Array.isArray(job.certificates)
    ? [...(job.certificates as unknown[])]
        .map((c) => Number(c))
        .sort((a, b) => a - b)
    : [];
  return JSON.stringify({
    skill_id: job.skill_id ?? null,
    address_id: job.address_id ?? null,
    contact_id: job.contact_id ?? null,
    uniform_id: job.uniform_id ?? null,
    job_type: String(job.job_type ?? '').toUpperCase(),
    is_holiday: !!job.is_holiday,
    instructions: normalizeTemplateText(job.instructions),
    address_instructions: normalizeTemplateText(job.address_instructions),
    contact_instructions: normalizeTemplateText(job.contact_instructions),
    uniform_instructions: normalizeTemplateText(job.uniform_instructions),
    share_contact_number: !!job.share_contact_number,
    all_shifts_same_worker: !!job.all_shifts_same_worker,
    auto_accept_requests: !!job.auto_accept_requests,
    certificates,
  });
};

export const dedupeTemplateJobs = <T extends Record<string, unknown>>(
  jobs: T[],
): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const job of jobs) {
    const key = templateJobIdentity(job);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(job);
  }
  return out;
};

// Auto-seed the customer's default certificate ids onto `jobs[0].certificates`
// — but only when the wizard wasn't prefilled from a template AND no
// certificates have been picked yet. Mirrors the legacy behavior of the
// `useCertificates` callback inside the orchestrator.
export const autoSeedCertificateDefaultsReducer = (
  prev: OrderStateV2,
  defaultIds: number[],
): OrderStateV2 => {
  if (prev.isPrefilled) return prev;
  if (prev.jobs.length === 0) return prev;
  const job = prev.jobs[0];
  if ((job.certificates || []).length > 0) return prev;
  const nextJobs = [...prev.jobs];
  nextJobs[0] = { ...job, certificates: defaultIds };
  return { ...prev, jobs: nextJobs };
};

// Start a new skill from the Review step. Prepends a fresh empty draft
// to `jobs[0]` so the wizard (which always reads/writes `jobs[0]`) edits
// the new skill while previously completed jobs are preserved at indices
// 1+. Mirrors the v1 `resetState() + ADD_JOB` split — without this, the
// wizard re-edits and overwrites the original `jobs[0]` instead of
// appending a separate job entry to the order.
export const addNewJobDraftReducer = (prev: OrderStateV2): OrderStateV2 => ({
  ...prev,
  jobs: [defaultJobDraftV2(prev.orderType || 'GIG'), ...prev.jobs],
});

// Cancel an in-progress new skill draft and fall back to the previously
// completed job(s). No-op when `jobs.length <= 1` so a single-skill order
// can't accidentally drop its only job — the wizard's existing "Cancel"
// button still handles that case by leaving the wizard altogether.
export const cancelNewJobDraftReducer = (prev: OrderStateV2): OrderStateV2 => {
  if (prev.jobs.length <= 1) return prev;
  return { ...prev, jobs: prev.jobs.slice(1) };
};

// Apply a fresh rate quote to `jobs[0]` — but only when the user hasn't
// already entered a pay/cost rate. Background refetches must never
// overwrite manual edits. Mirrors the legacy `useRateQuote` callback.
export const applyRateQuoteReducer = (
  prev: OrderStateV2,
  pay: number,
  cost: number,
): OrderStateV2 => {
  const job = prev.jobs[0];
  if (!job) return prev;
  if (job.pay_rate || job.cost_rate) return prev;
  const nextJobs = [...prev.jobs];
  nextJobs[0] = {
    ...job,
    pay_rate: pay,
    cost_rate: cost,
    original_pay_rate: pay,
    original_cost_rate: cost,
  };
  return { ...prev, jobs: nextJobs };
};
